"use client";

import React from "react";
import { Phone, Clock, Mail, CircleCheck, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
import { useFormContext } from "react-hook-form";
declare global {
  interface Window {
    wvcClient: {
      sendEmail: (options: {
        to: string;
        subject: string;
        body: string;
      }) => Promise<void>;
    };
  }
}
const shared_data = {
  business_name: "GovERP",
  phone: "+1 (800) GOV-ERP1",
  email: "solutions@goverp.io",
  address: "1700 Pennsylvania Avenue NW, Washington, DC",
  hours: "Mon-Fri: 8:00 AM - 6:00 PM EST"
};
export default function ProfessionalConsultation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return;
}
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  const {
    getValues
  } = useFormContext();
  const handleExtendedSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    await handleSubmit(e);
    const values = getValues();
    if (!values.full_name || !values.email) return;
    const emailBody = `
First Name: ${values.full_name}
Agency Email: ${values.email}
Direct Phone: ${values.phone}
Agency / Organization: ${values.company}
Professional Role: ${values.contact_preference}
How Can We Help You?: ${values.message}

Submission Date/Time: ${new Date().toLocaleString()}
    `.trim();
    try {
      const client = typeof window !== 'undefined' && window.wvcClient ? window.wvcClient : {
        sendEmail: async (options: {
          to: string;
          subject: string;
          body: string;
        }) => {
          console.warn("wvcClient not found. Fallback triggered for email simulation:", options);
          return Promise.resolve();
        }
      };
      await client.sendEmail({
        to: "info@kanha.biz",
        subject: "Schedule a Free Consultation from Kanha website request",
        body: emailBody
      });
    } catch (error) {
      console.error("Failed to send consultation request email:", error);
    }
  };
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center text-center py-12 space-y-4 animate-in fade-in zoom-in duration-300">
        <div className="bg-green-100 dark:bg-green-900/30 p-4 rounded-full">
          <CircleCheck className="size-12 text-green-600 dark:text-green-400" />
        </div>
        <h4 className="text-xl font-bold text-foreground">Request Received</h4>
        <p className="text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-[#095fa3] text-[#095fa3]">
          Send Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleExtendedSubmit} className="space-y-4">
      <FormField control={control} name="full_name" rules={{
      required: "First Name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[#3f444b] dark:text-foreground font-medium">First Name</FormLabel>
            <FormControl>
              <Input placeholder="John Doe" className="border-[#e0e0e0] focus:ring-[#095fa3] rounded-md" {...field} />
            </FormControl>
          </FormItem>} />

      <div className="grid grid-cols-1 gap-4">
        <FormField control={control} name="contact_preference" rules={{
        required: "Please select your role"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[#3f444b] dark:text-foreground font-medium">Professional Role</FormLabel>
              <FormControl>
                <div className="relative">
                  <select className={cn("flex h-11 w-full items-center justify-between rounded-md border border-[#e0e0e0] bg-transparent px-3 py-1 text-sm shadow-xs ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-[#095fa3] disabled:cursor-not-allowed disabled:opacity-50", "appearance-none")} {...field}>
                    <option value="Director">Agency Director</option>
                    <option value="IT Manager">IT Administrator</option>
                    <option value="Compliance">Compliance Officer</option>
                    <option value="Other">Other Stakeholder</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
                    <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </FormControl>
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="email" rules={{
        required: "Agency email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[#3f444b] dark:text-foreground font-medium">Agency Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="j.doe@agency.gov" className="border-[#e0e0e0] focus:ring-[#095fa3] rounded-md" {...field} />
              </FormControl>
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[#3f444b] dark:text-foreground font-medium">Direct Phone</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" className="border-[#e0e0e0] focus:ring-[#095fa3] rounded-md" {...field} />
              </FormControl>
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 gap-4">
        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[#3f444b] dark:text-foreground font-medium">Agency / Organization</FormLabel>
              <FormControl>
                <Input placeholder="Department of Operations" className="border-[#e0e0e0] focus:ring-[#095fa3] rounded-md" {...field} />
              </FormControl>
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Please provide some details"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[#3f444b] dark:text-foreground font-medium">How Can We Help You?</FormLabel>
            <FormControl>
              <textarea className={cn("flex min-h-[100px] w-full rounded-md border border-[#e0e0e0] bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#095fa3] disabled:cursor-not-allowed disabled:opacity-50 resize-y")} placeholder="Briefly describe your agency's modernization needs..." {...field} />
            </FormControl>
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-sm text-destructive font-medium">{submitError}</p>
        </div>}

      <Button type="submit" className="w-full h-12 text-base font-bold shadow-md bg-[#fb8c26] hover:bg-[#e07b1f] text-white rounded-[4px] transition-all duration-300" disabled={isSubmitting}>
        {isSubmitting ? <>
            <Loader className="mr-2 h-4 w-4 animate-spin" />
            Validating...
          </> : "Submit"}
      </Button>
    </Form>;
}